import React, { useState } from "react";
import "./terminal-row.css";
import StatusBadge, {
  StatusVariant,
} from "components/status-badge/StatusBadge";
import OverflowMenu, {
  OverflowMenuAction,
} from "components/overflow-menu/OverflowMenu";
import LoadingModal from "modals/loading/LoadingModal";
import ErrorModal from "modals/error-modal/ErrorModal";
import SuccessModal from "modals/success-modal/SuccessModal";
import { useBlockTerminal } from "features/terminals/useBlockTerminal";
import {
  AngleDown,
  BanIconV1,
  CheckCircleIcon,
  CopyIconV1,
} from "images/image-component";
import { posTypeName, posVendorName } from "utils/pos";

export interface TerminalRecord {
  serialId: string;
  serial: string;
  merchantId: string;
  merchantName: string;
  merchantNamePending?: boolean;
  status: StatusVariant;
  registeredAt: number;
  vendorDigit: number;
  typeDigit: number;
}

function TerminalRow({
  serialId,
  serial,
  merchantId,
  merchantName,
  merchantNamePending,
  status,
  registeredAt,
  vendorDigit,
  typeDigit,
}: TerminalRecord) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const hasMerchant = !!merchantId && merchantId !== "0";

  const copyMerchantId = () => {
    navigator.clipboard?.writeText(merchantId).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    });
  };
  const {
    blockingTerminal,
    blockingTerminalMsg,
    blockTerminalError,
    blockTerminalErrorMsg,
    blockTerminalSuccess,
    blockTerminalSuccessMsg,
    handleBlockTerminal,
    handleRetry,
    handleCloseError,
    handleCloseSuccess,
  } = useBlockTerminal();
console.log(`TerminalRow merchantName: `, merchantName);
  // blockPOS reverts from any other status — the chain allows it only from ACTIVE or IDLE.
  const normalizedStatus = status?.toLowerCase();
  const canBlockTerminal =
    normalizedStatus === "active" || normalizedStatus === "idle";

  const overflowActions: OverflowMenuAction[] = [
    {
      label: "Block terminal",
      icon: <BanIconV1 size={16} color="var(--neg)" />,
      onClick: () => handleBlockTerminal(serialId),
      disabled: !canBlockTerminal,
    },
  ];

  return (
    <div className="tm-row">
      <span className="tm-serial">{serial}</span>
      <span
        className="tm-merchant"
        title={merchantNamePending ? undefined : merchantName}
      >
        {merchantNamePending ? (
          <span
            className="tm-skeleton-bar tm-skeleton-bar--merchant"
            aria-hidden="true"
          />
        ) : (
          merchantName
        )}
      </span>
      <span className="tm-status">
        <StatusBadge status={status} />
      </span>

      <div className="tm-actions">
        <OverflowMenu
          actions={overflowActions}
          ariaLabel="More terminal actions"
        />
        <button
          type="button"
          className={`tm-chevron${expanded ? " tm-chevron--open" : ""}`}
          onClick={() => setExpanded((e) => !e)}
          aria-label={expanded ? "Hide details" : "Show details"}
          aria-expanded={expanded}
        >
          <AngleDown size={16} />
        </button>
      </div>

      {expanded && (
        <div className="tm-detail">
          <div className="tm-detail-item">
            <span className="tm-detail-k">Merchant ID</span>
            {/* Stock terminals carry merchantId "0" — there is no merchant to show. */}
            <span className="tm-detail-v">
              {hasMerchant ? (
                <>
                  <span className="tm-mono">{merchantId}</span>
                  <button
                    type="button"
                    className="tm-copy-btn"
                    onClick={copyMerchantId}
                    aria-label="Copy merchant id"
                  >
                    {copied ? (
                      <CheckCircleIcon size={14} />
                    ) : (
                      <CopyIconV1 size={14} />
                    )}
                  </button>
                </>
              ) : (
                "—"
              )}
            </span>
          </div>
          <div className="tm-detail-item">
            <span className="tm-detail-k">Type</span>
            <span className="tm-detail-v">{posTypeName(typeDigit)}</span>
          </div>
          <div className="tm-detail-item">
            <span className="tm-detail-k">Vendor</span>
            <span className="tm-detail-v">{posVendorName(vendorDigit)}</span>
          </div>
          <div className="tm-detail-item">
            <span className="tm-detail-k">Registered</span>
            {/* registeredAt is a chain (block.timestamp) value — seconds, not ms. */}
            <span className="tm-detail-v">
              {new Date(registeredAt * 1000).toLocaleDateString()}
            </span>
          </div>
        </div>
      )}

      {blockingTerminal && (
        <LoadingModal isOpen={blockingTerminal} loadingMsg={blockingTerminalMsg} />
      )}
      {blockTerminalError && (
        <ErrorModal
          isOpen={blockTerminalError}
          errorMsg={blockTerminalErrorMsg}
          handleClose={handleCloseError}
          tryAgain={handleRetry}
        />
      )}
      {blockTerminalSuccess && (
        <SuccessModal
          isOpen={blockTerminalSuccess}
          message={blockTerminalSuccessMsg}
          handleClose={handleCloseSuccess}
        />
      )}
    </div>
  );
}

export default TerminalRow;
